// Translation category mapping based on the pl-PL.ts structure
export const translationCategories = {
  general: {
    name: 'Ogólne',
    icon: 'GlobeIcon',
    color: 'primary',
    keys: ['button', 'general', 'greeting', 'greeting_modal', 'actions', 'back_main_page'],
  },
  auth: {
    name: 'Autoryzacja',
    icon: 'LockIcon',
    color: 'warning',
    keys: ['auth', 'login', 'register'],
  },
  navigation: {
    name: 'Nawigacja',
    icon: 'MenuIcon',
    color: 'info',
    keys: ['pages', 'icon', 'footer', 'pagination'],
  },
  forms: {
    name: 'Formularze',
    icon: 'EditIcon',
    color: 'success',
    keys: ['inputs', 'validation', 'consents', 'drag_and_drop'],
  },
  ecommerce: {
    name: 'E-commerce',
    icon: 'ShoppingCartIcon',
    color: 'danger',
    keys: ['product', 'purchase_process', 'availability', 'newsletter', 'filters'],
  },
  content: {
    name: 'Treść',
    icon: 'FileTextIcon',
    color: 'secondary',
    keys: ['blog', 'pressroom', 'downloadable_materials', 'inspirations', 'homepage'],
  },
  contact: {
    name: 'Kontakt',
    icon: 'PhoneIcon',
    color: 'dark',
    keys: ['contact', 'cooperation', 'service', 'customer-support', 'signaller'],
  },
  locations: {
    name: 'Lokalizacje',
    icon: 'MapPinIcon',
    color: 'light',
    keys: ['where_to_buy', 'foreign_distribution', 'county', 'countries', 'countries_names'],
  },
  company: {
    name: 'Firma',
    icon: 'BuildingIcon',
    color: 'primary',
    keys: ['job_offers', 'components', 'static_page_content', 'shareholders_communication', 'designer_zone'],
  },
  products: {
    name: 'Produkty',
    icon: 'PackageIcon',
    color: 'success',
    keys: ['productPage', 'catalogue', 'umywalka20promo'],
  },
  profile: {
    name: 'Profil użytkownika',
    icon: 'UserIcon',
    color: 'info',
    keys: ['my_profile', 'user_avatar'],
  },
  dashboard: {
    name: 'Panel',
    icon: 'HomeIcon',
    color: 'warning',
    keys: ['dashboard'],
  },
  languages: {
    name: 'Języki',
    icon: 'GlobeIcon',
    color: 'secondary',
    keys: ['languages'],
  },
  emergency: {
    name: 'Tryb awaryjny',
    icon: 'AlertTriangleIcon',
    color: 'danger',
    keys: ['emergencyMode'],
  },
};

// Helper function to get category for a translation key
export const getCategoryForKey = (key) => {
  for (const [categoryId, category] of Object.entries(translationCategories)) {
    if (category.keys.includes(key)) {
      return categoryId;
    }
  }
  return 'general'; // default category
};

// Helper function to flatten nested object
export const flattenTranslations = (obj, prefix = '') => {
  const flattened = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        Object.assign(flattened, flattenTranslations(obj[key], newKey));
      } else {
        flattened[newKey] = obj[key];
      }
    }
  }

  return flattened;
};

// Helper function to unflatten object back to nested structure
export const unflattenTranslations = (obj) => {
  const result = {};

  // First, detect potential conflicts
  const conflictingKeys = new Set();
  const keySet = Object.keys(obj);

  keySet.forEach((key) => {
    const parts = key.split('.');
    for (let i = 1; i < parts.length; i++) {
      const parentKey = parts.slice(0, i).join('.');
      if (keySet.includes(parentKey)) {
        conflictingKeys.add(parentKey);
        conflictingKeys.add(key);
      }
    }
  });

  // Sort keys to ensure parent keys are processed before child keys
  const sortedKeys = Object.keys(obj).sort();

  for (const key of sortedKeys) {
    if (obj.hasOwnProperty(key)) {
      const keys = key.split('.');
      let current = result;

      for (let i = 0; i < keys.length - 1; i++) {
        const currentKey = keys[i];

        if (!current[currentKey]) {
          current[currentKey] = {};
        } else if (typeof current[currentKey] !== 'object' || current[currentKey] === null) {
          // If the current value is not an object (e.g., it's a string), we have a conflict
          // Log the conflict for debugging
          console.warn(`Translation key conflict detected: '${currentKey}' exists as both a value and parent key`);

          // Skip this problematic translation to prevent the error
          continue;
        }
        current = current[currentKey];
      }

      const lastKey = keys[keys.length - 1];

      // Check if setting this key would overwrite an existing object
      if (current[lastKey] && typeof current[lastKey] === 'object' && current[lastKey] !== null) {
        console.warn(`Translation key conflict detected: '${key}' would overwrite existing nested structure`);
        continue;
      }

      current[lastKey] = obj[key];
    }
  }

  return result;
};

// Helper function to group translations by category
export const groupTranslationsByCategory = (translations) => {
  const grouped = {};

  // Initialize all categories
  Object.keys(translationCategories).forEach((categoryId) => {
    grouped[categoryId] = {};
  });

  // Group translations by category
  Object.keys(translations).forEach((key) => {
    const topLevelKey = key.split('.')[0];
    const categoryId = getCategoryForKey(topLevelKey);
    grouped[categoryId][key] = translations[key];
  });

  return grouped;
};

// Helper function to search in translations
export const searchTranslations = (translations, searchTerm) => {
  if (!searchTerm) return translations;

  const filtered = {};
  const lowerSearchTerm = searchTerm.toLowerCase();

  Object.keys(translations).forEach((key) => {
    const value = translations[key];
    if (
      key.toLowerCase().includes(lowerSearchTerm) ||
      (typeof value === 'string' && value.toLowerCase().includes(lowerSearchTerm))
    ) {
      filtered[key] = value;
    }
  });

  return filtered;
};

// Helper function to validate translation value
export const validateTranslationValue = (value) => {
  const errors = [];

  if (typeof value !== 'string') {
    errors.push('Wartość musi być tekstem');
  }

  if (value && value.length > 5000) {
    errors.push('Wartość nie może być dłuższa niż 5000 znaków');
  }

  // Check for potential HTML injection
  if (value && /<script|javascript:/i.test(value)) {
    errors.push('Wartość zawiera niedozwolone elementy');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Helper function to export translations to different formats
export const exportToFormat = (translations, format = 'json') => {
  switch (format) {
    case 'json':
      try {
        return JSON.stringify(unflattenTranslations(translations), null, 2);
      } catch (error) {
        console.error('Error unflattening translations for export, using flat structure:', error);
        return JSON.stringify(translations, null, 2);
      }
    case 'csv':
      const rows = [['Key', 'Value']];
      Object.keys(translations).forEach((key) => {
        rows.push([key, translations[key]]);
      });
      return rows.map((row) => row.join(',')).join('\n');
    default:
      return JSON.stringify(translations, null, 2);
  }
};

// Helper function to calculate missing translations statistics
export const calculateMissingTranslationsStats = (allLocaleTranslations, availableLanguages) => {
  const stats = {
    byCategory: {},
    byLocale: {},
    total: 0,
    incomplete: 0,
  };

  // Get all unique keys from all locales
  const allKeys = new Set();
  Object.values(allLocaleTranslations).forEach((translations) => {
    Object.keys(translations).forEach((key) => allKeys.add(key));
  });

  // Initialize category stats
  Object.keys(translationCategories).forEach((categoryId) => {
    stats.byCategory[categoryId] = {
      total: 0,
      missing: 0,
      complete: 0,
      percentage: 100,
    };
  });

  // Initialize locale stats
  availableLanguages.forEach((lang) => {
    stats.byLocale[lang.locale] = {
      total: allKeys.size,
      missing: 0,
      complete: 0,
      percentage: 100,
    };
  });

  // Calculate missing translations
  allKeys.forEach((key) => {
    const topLevelKey = key.split('.')[0];
    const categoryId = getCategoryForKey(topLevelKey);

    // Update category totals
    stats.byCategory[categoryId].total++;

    availableLanguages.forEach((lang) => {
      const locale = lang.locale;
      const translations = allLocaleTranslations[locale] || {};
      const hasTranslation = translations[key] && translations[key].trim() !== '';

      if (!hasTranslation) {
        stats.byLocale[locale].missing++;
        // Only count missing translations for non-Polish languages in category stats
        if (lang.lang !== 'pl') {
          stats.byCategory[categoryId].missing++;
          stats.total++;
        }
      } else {
        stats.byLocale[locale].complete++;
        // Only count complete translations for non-Polish languages in category stats
        if (lang.lang !== 'pl') {
          stats.byCategory[categoryId].complete++;
        }
      }
    });
  });

  // Calculate percentages
  Object.keys(stats.byCategory).forEach((categoryId) => {
    const category = stats.byCategory[categoryId];
    // Only consider non-Polish languages for category percentage calculation
    const nonPolishLanguages = availableLanguages.filter((lang) => lang.lang !== 'pl');
    const totalPossible = category.total * nonPolishLanguages.length;
    category.percentage = totalPossible > 0 ? Math.round((category.complete / totalPossible) * 100) : 100;
  });

  Object.keys(stats.byLocale).forEach((locale) => {
    const localeStats = stats.byLocale[locale];
    localeStats.percentage = localeStats.total > 0 ? Math.round((localeStats.complete / localeStats.total) * 100) : 100;
  });

  // Count incomplete categories (less than 100%)
  stats.incomplete = Object.values(stats.byCategory).filter((cat) => cat.percentage < 100).length;

  return stats;
};

// Helper function to filter incomplete translations in a category
export const filterIncompleteTranslations = (translations, getTranslationsForKey, availableLanguages) => {
  const incomplete = {};

  Object.keys(translations).forEach((key) => {
    const allTranslationsForKey = getTranslationsForKey(key);
    let hasIncompleteTranslations = false;

    // Check if any language is missing or empty
    availableLanguages.forEach((lang) => {
      if (lang.lang !== 'pl') {
        // Skip Polish as it's the source language
        const translation = allTranslationsForKey[lang.locale];
        if (!translation || translation.trim() === '') {
          hasIncompleteTranslations = true;
        }
      }
    });

    if (hasIncompleteTranslations) {
      incomplete[key] = translations[key];
    }
  });

  return incomplete;
};
